import jsPDF from 'jspdf';
import 'jspdf-autotable';

const COMPANY = process.env.REACT_APP_COMPANY_NAME || 'Bhavna Corp';

export function generateEmployeePDF(attempt, employee, mcqAnswers, scenarioAnswers, aiAnalysis) {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.getWidth();
  let y = 15;

  // Header
  doc.setFillColor(8, 11, 18);
  doc.rect(0, 0, w, 40, 'F');
  doc.setTextColor(59, 130, 246);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`${COMPANY} — AI Assessment Report`, 14, y + 8);
  doc.setFontSize(10);
  doc.setTextColor(148, 163, 184);
  doc.text('Individual Performance Report', 14, y + 16);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, w - 14, y + 16, { align: 'right' });

  y = 50;

  // Employee Info
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Employee Details', 14, y);
  y += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const info = [
    ['Name', employee.name || '—'],
    ['Email', employee.email],
    ['Department', employee.department || '—'],
    ['Date', new Date(attempt.started_at).toLocaleString()],
    ['Time Used', `${Math.floor(attempt.time_used_seconds / 60)}m ${attempt.time_used_seconds % 60}s`],
    ['Tab Switches', String(attempt.tab_switches || 0)],
  ];
  doc.autoTable({
    startY: y,
    head: [],
    body: info,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 } },
    margin: { left: 14 },
  });
  y = doc.lastAutoTable.finalY + 10;

  // Score Summary
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Score Summary', 14, y);
  y += 4;
  const scoreColor = attempt.passed ? [16, 185, 129] : [239, 68, 68];
  doc.autoTable({
    startY: y,
    head: [['Metric', 'Score', 'Status']],
    body: [
      ['MCQ Score', `${attempt.mcq_score}% (${attempt.mcq_correct}/${attempt.mcq_total})`, attempt.mcq_score >= 70 ? 'Good' : 'Needs Work'],
      ['Written Score', `${attempt.scenario_score}%`, attempt.scenario_score >= 70 ? 'Good' : 'Needs Work'],
      ['Overall', `${attempt.total_score}%`, attempt.passed ? 'PASSED' : 'FAILED'],
    ],
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 4 },
    headStyles: { fillColor: [20, 30, 54], textColor: [255, 255, 255] },
    margin: { left: 14 },
  });
  y = doc.lastAutoTable.finalY + 10;

  // AI Analysis
  if (aiAnalysis) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('AI-Powered Analysis', 14, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    if (aiAnalysis.overall_assessment) {
      doc.text(doc.splitTextToSize(`Assessment: ${aiAnalysis.overall_assessment}`, w - 28), 14, y);
      y += doc.splitTextToSize(`Assessment: ${aiAnalysis.overall_assessment}`, w - 28).length * 5 + 4;
    }
    if (aiAnalysis.prompt_readiness_level) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Readiness Level: ${aiAnalysis.prompt_readiness_level}`, 14, y);
      y += 6;
    }
    if (aiAnalysis.strongest_areas?.length) {
      doc.setFont('helvetica', 'normal');
      doc.text(`Strengths: ${aiAnalysis.strongest_areas.join(', ')}`, 14, y);
      y += 6;
    }
    if (aiAnalysis.weakest_areas?.length) {
      doc.text(`Weak Areas: ${aiAnalysis.weakest_areas.join(', ')}`, 14, y);
      y += 6;
    }
    if (aiAnalysis.recommendations?.length) {
      doc.text('Recommendations:', 14, y);
      y += 5;
      aiAnalysis.recommendations.forEach((r, i) => {
        const lines = doc.splitTextToSize(`${i + 1}. ${r}`, w - 32);
        doc.text(lines, 18, y);
        y += lines.length * 5 + 2;
      });
    }
    y += 4;
  }

  // MCQ Details — new page if needed
  if (y > 230) { doc.addPage(); y = 15; }
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`MCQ Results (${attempt.mcq_correct}/${attempt.mcq_total} correct)`, 14, y);
  y += 4;

  const mcqRows = mcqAnswers.map(a => [
    a.category,
    a.question_text.slice(0, 60) + (a.question_text.length > 60 ? '...' : ''),
    a.is_correct ? '✓' : '✗',
    a.is_correct ? '' : (a.options?.[a.correct_option] || '').slice(0, 30),
  ]);

  doc.autoTable({
    startY: y,
    head: [['Category', 'Question', 'Result', 'Correct Answer']],
    body: mcqRows,
    theme: 'striped',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [20, 30, 54], textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 80 },
      2: { cellWidth: 15, halign: 'center' },
      3: { cellWidth: 50 },
    },
    margin: { left: 14 },
  });
  y = doc.lastAutoTable.finalY + 10;

  // Written Scenarios
  doc.addPage();
  y = 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Written Scenario Responses', 14, y);
  y += 8;

  scenarioAnswers.forEach((s, i) => {
    if (y > 240) { doc.addPage(); y = 15; }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Scenario ${i + 1}: ${s.scenario_title} (${s.category}) — ${s.ai_score || 0}%`, 14, y);
    y += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    const scnLines = doc.splitTextToSize(s.scenario_text.split('\n')[0].slice(0, 120) + '...', w - 28);
    doc.text(scnLines, 14, y);
    y += scnLines.length * 4 + 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Response:', 14, y);
    y += 5;
    const resp = s.response || '(No response)';
    const respLines = doc.splitTextToSize(resp, w - 28);
    doc.text(respLines.slice(0, 20), 14, y);
    y += Math.min(respLines.length, 20) * 4 + 4;

    if (s.ai_feedback) {
      doc.setFont('helvetica', 'bold');
      doc.text('AI Feedback:', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      const fbLines = doc.splitTextToSize(s.ai_feedback, w - 28);
      doc.text(fbLines, 14, y);
      y += fbLines.length * 4 + 4;
    }

    if (s.ai_rubric_hits?.length) {
      doc.text(`Criteria Met: ${s.ai_rubric_hits.join(', ')}`, 14, y);
      y += 6;
    }
    y += 4;
  });

  // Footer
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`${COMPANY} PE Assessment — Confidential — Page ${i}/${pages}`, w / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
  }

  doc.save(`PE_Report_${employee.email.split('@')[0]}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
